import React, { useRef, useState, useEffect, useMemo } from 'react';
import { api } from '../scripts/api';
import { Camera, Truck, CheckCircle, AlertTriangle } from 'lucide-react';
import CardComponent from "../components/CardComponent";

function toNumId(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function DriverPage() {
  const inputRef = useRef(null);
  const [vin, setVin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const [moves, setMoves] = useState([]);
  const [invById, setInvById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Driver check-in
  const [checkInLocation, setCheckInLocation] = useState('');
  const [checkInStatus, setCheckInStatus] = useState('available');
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [checkInMsg, setCheckInMsg] = useState('');

  // Get driver id from session (set when driver logs in via driver auth)
  const driverId = (() => {
    try { return JSON.parse(sessionStorage.getItem('driver_user') || 'null')?.DriverId || null; }
    catch { return null; }
  })();

  useEffect(() => {
    loadMoves();
  }, []);

const loadMoves = async () => {
  setLoading(true);
  setError('');
  try {
    const { data } = await api.get("/driver/moves");
    const list = Array.isArray(data) ? data.filter((m) => !m.completed_at) : [];
    setMoves(list);
    await hydrateInventoryForMoves(list);
  } catch (e) {
    setError(e?.response?.data?.error || "Failed to load moves.");
    setMoves([]);
  } finally {
    setLoading(false);
  }
};

const hydrateInventoryForMoves = async (list) => {
  const ids = Array.from(
    new Set(
      (list || [])
        .map((m) => toNumId(m.inventory_id))
        .filter((n) => n != null)
    )
  );

  const missing = ids.filter((id) => !(id in invById));
  if (!missing.length) return;

  const fetched = await Promise.all(
    missing.map(async (id) => {
      try {
        const { data } = await api.get(`/driver/inventory/${id}`);
        return [id, data || null];
      } catch {
        return [id, null];
      }
    })
  );

  setInvById((prev) => {
    const next = { ...prev };
    for (const [id, rec] of fetched) next[id] = rec;
    return next;
  });
};

  const scan = async (file) => {
    setBusy(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/ocr', fd, { headers: { 'Content-Type': 'multipart/form-data' }});
      setVin((data.candidates || [])[0] || '');
    } catch (e) {
      console.error('Driver OCR failed', e);
      setMsg('OCR failed');
    } finally {
      setBusy(false);
    }
  };

  const act = async (endpoint) => {
    if (!vin) { setMsg('No VIN'); return; }
    setBusy(true); setMsg('');
    try {
      const { data } = await api.post(endpoint, { vin });
      setMsg(`Updated: ${data.updated || 0}`);
    } catch (e) {
      console.error('Driver action failed', e);
      setMsg('Action failed');
    } finally {
      setBusy(false);
    }
  };

  const moveCards = useMemo(() => {
    return moves.map((m) => {
      const invId = toNumId(m.inventory_id);
      const invRec = invId != null ? invById[invId] : null;
      return { m, invId, invRec };
    });
  }, [moves, invById]);

  const completeMove = async (tripId) => {
    setBusy(true);
    setMsg('');
    try {
      await api.post(`/moves/${tripId}/complete`);
      setMsg('Move completed');
      await loadMoves();
    } catch (e) {
      console.error('complete move failed', e);
      setMsg(e?.response?.data?.error || 'Complete failed');
    } finally {
      setBusy(false);
    }
  };

  const submitCheckIn = async () => {
    if (!driverId) { setCheckInMsg('No driver ID in session — contact dispatch.'); return; }
    setCheckInBusy(true); setCheckInMsg('');
    try {
      await api.patch(`/driver/${driverId}/checkin`, {
        current_location: checkInLocation,
        driver_status: checkInStatus,
      });
      setCheckInMsg('Status updated.');
    } catch (e) {
      setCheckInMsg(e?.response?.data?.error || 'Check-in failed');
    } finally {
      setCheckInBusy(false);
    }
  };

  return (
    <div className="container col">
      <div className="splash">
        <div>
          <div className="splash-title">Driver Tools</div>
          <div className="splash-sub">Scan VIN • Update Status</div>

          <div className="scan-grid">
            <button className="scan-tile" onClick={() => inputRef.current?.click()}>
              <div className="scan-tile-top">
                <div className="scan-icon"><Camera size={22} /></div>
                <div className="scan-chev">↗</div>
              </div>
              <div className="scan-title">Scan VIN</div>
              <div className="scan-sub">Use camera</div>
            </button>

            <button className="scan-tile" onClick={() => act('/driver/mark_picked_up')}>
              <div className="scan-tile-top">
                <div className="scan-icon"><Truck size={22} /></div>
                <div className="scan-chev">↗</div>
              </div>
              <div className="scan-title">Mark Picked Up</div>
              <div className="scan-sub">Update status</div>
            </button>

            <button className="scan-tile" onClick={() => act('/driver/mark_delivered')}>
              <div className="scan-tile-top">
                <div className="scan-icon"><CheckCircle size={22} /></div>
                <div className="scan-chev">↗</div>
              </div>
              <div className="scan-title">Mark Delivered</div>
              <div className="scan-sub">Update status</div>
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            <input className="input" value={vin} onChange={(e) => setVin(e.target.value)} placeholder="VIN or last-8" />
          </div>
        </div>
      </div>

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
             onChange={(e) => e.target.files?.[0] && scan(e.target.files[0])} style={{ display: 'none' }} />

      {busy && <div className="muted">Working…</div>}
      {msg && (
        <div className="row" style={{ marginTop: 10, gap: 8, alignItems: "center" }}>
          <AlertTriangle size={16} />
          <div className="muted">{msg}</div>
        </div>
      )}

      {/* Driver check-in panel */}
      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>My Status / Location</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <select
              className="input"
              value={checkInStatus}
              onChange={(e) => setCheckInStatus(e.target.value)}
              style={{ maxWidth: 180 }}
            >
              <option value="available">Available</option>
              <option value="on_move">On Move</option>
              <option value="off_duty">Off Duty</option>
            </select>
            <input
              className="input"
              placeholder="Current location (e.g. Calgary Yard)"
              value={checkInLocation}
              onChange={(e) => setCheckInLocation(e.target.value)}
            />
            <button className="btn" onClick={submitCheckIn} disabled={checkInBusy}>
              Update
            </button>
          </div>
          {checkInMsg && <div className="muted">{checkInMsg}</div>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Your Assigned Moves</div>
        {loading ? <div className="muted">Loading…</div> : null}
        {error ? <div className="muted">{error}</div> : null}

        <div style={{ display: "grid", gap: 10 }}>
          {moveCards.map(({ m, invId, invRec }) => (
            <div key={m.TripId} className="card" style={{ background: "rgba(255,255,255,0.02)" }}>
              <div
                className="row"
                style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <div>
                  <div style={{ fontWeight: 900 }}>Move #{m.TripId}</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Inventory: {m.inventory_id || "(none)"} • Destination: {m.destination || "(none)"}
                  </div>
                </div>
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button className="btn" onClick={() => completeMove(m.TripId)} disabled={busy}>
                    Mark Completed
                  </button>
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <CardComponent record={invRec || (invId != null ? { id: invId } : {})} table="all_inventory" />
                {invId != null && invRec === null ? (
                  <div className="muted" style={{ marginTop: 8 }}>
                    Couldn't load inventory record #{invId}.
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {!loading && moves.length === 0 ? <div className="muted">No assigned moves.</div> : null}
        </div>
      </div>
    </div>
  );
}
